import { useCallback, useEffect, useState } from 'react'

import { useAuth } from '../../_hooks/use-auth'
import {
  getCachedQuickPassPushToken,
  requestQuickPassPushPermission,
  unregisterQuickPassPush,
} from '../../_data/quickpass-push'
import {
  ActionRow,
  Description,
  PrimaryButton,
  SecondaryButton,
  StatusChip,
  TileLabel,
  TileRoot,
  TileTopRow,
} from './styled'
import { PushPermissionTileState, PushPermissionUiStatus } from './types'

const initialState = (): PushPermissionTileState => {
  if (typeof Notification === 'undefined') {
    return {
      status: 'unsupported',
      message: 'This browser doesn’t support push notifications.',
    }
  }

  if (Notification.permission === 'granted') {
    if (getCachedQuickPassPushToken() !== null) {
      return {
        status: 'enabled',
        message: 'Push is on for this device.',
      }
    }
    return {
      status: 'idle',
      message: 'Permission granted — tap Enable to register this device.',
    }
  }

  if (Notification.permission === 'denied') {
    return {
      status: 'denied',
      message:
        'You blocked push in your browser. Re-enable it in site settings, then come back.',
    }
  }

  return {
    status: 'idle',
    message:
      'Get a heads-up the moment a new listing matches your search — even when QuickPass is closed.',
  }
}

const tone = (
  status: PushPermissionUiStatus,
): 'idle' | 'enabled' | 'denied' | 'neutral' => {
  if (status === 'enabled') {
    return 'enabled'
  }
  if (status === 'denied') {
    return 'denied'
  }
  if (status === 'idle' || status === 'requesting') {
    return 'idle'
  }
  return 'neutral'
}

const statusLabel = (status: PushPermissionUiStatus): string => {
  if (status === 'enabled') {
    return 'On'
  }
  if (status === 'denied') {
    return 'Blocked'
  }
  if (status === 'requesting') {
    return 'Asking…'
  }
  if (status === 'unsupported') {
    return 'Not supported'
  }
  if (status === 'misconfigured') {
    return 'Not set up'
  }
  if (status === 'error') {
    return 'Error'
  }
  return 'Off'
}

/**
 * Profile-page tile for managing push notifications on the current device.
 * Walks the renter through the browser permission prompt, registers the FCM
 * token with the backend, and offers a one-click disable that drops the
 * token both locally and server-side.
 *
 * @returns {JSX.Element}
 */
const PushPermissionTile = (): JSX.Element => {
  const { quickPassUser } = useAuth()
  const [state, setState] = useState<PushPermissionTileState>(initialState)
  const [isBusy, setIsBusy] = useState<boolean>(false)

  useEffect(() => {
    setState(initialState())
  }, [quickPassUser?.id])

  const handleEnable = useCallback(async (): Promise<void> => {
    if (quickPassUser === null) {
      return
    }

    setIsBusy(true)
    setState(previous => ({ ...previous, status: 'requesting' }))

    const outcome = await requestQuickPassPushPermission(quickPassUser.id)

    if (outcome.status === 'registered') {
      setState({
        status: 'enabled',
        message: 'Push is on for this device.',
      })
    } else if (outcome.status === 'denied') {
      setState({
        status: 'denied',
        message:
          'Permission denied. You can re-enable push from your browser site settings.',
      })
    } else if (outcome.status === 'unsupported') {
      setState({
        status: 'unsupported',
        message: 'This browser doesn’t support push notifications.',
      })
    } else if (outcome.status === 'misconfigured') {
      setState({
        status: 'misconfigured',
        message: outcome.reason,
      })
    } else {
      setState({
        status: 'error',
        message: outcome.errorMessage,
      })
    }

    setIsBusy(false)
  }, [quickPassUser])

  const handleDisable = useCallback(async (): Promise<void> => {
    if (quickPassUser === null) {
      return
    }

    setIsBusy(true)
    await unregisterQuickPassPush(quickPassUser.id)
    setState({
      status: 'idle',
      message: 'Push is off for this device. You can turn it back on anytime.',
    })
    setIsBusy(false)
  }, [quickPassUser])

  return (
    <TileRoot>
      <TileTopRow>
        <TileLabel>Browser push on this device</TileLabel>
        <StatusChip tone={tone(state.status)}>
          {statusLabel(state.status)}
        </StatusChip>
      </TileTopRow>
      <Description>{state.message}</Description>
      <ActionRow>
        {state.status === 'enabled' ? (
          <SecondaryButton
            type="button"
            disabled={isBusy}
            onClick={() => {
              void handleDisable()
            }}
          >
            Turn push off
          </SecondaryButton>
        ) : (
          <PrimaryButton
            type="button"
            disabled={
              isBusy ||
              state.status === 'denied' ||
              state.status === 'unsupported' ||
              state.status === 'misconfigured'
            }
            onClick={() => {
              void handleEnable()
            }}
          >
            {isBusy === true ? 'Working…' : 'Enable push'}
          </PrimaryButton>
        )}
      </ActionRow>
    </TileRoot>
  )
}

export default PushPermissionTile
